// Design-Berater widget. Talks to the design-berater Cloudflare Worker,
// keeps conversation state in sessionStorage, and emits GA4 events
// (advisor_open, advisor_message, advisor_recommendation_shown,
// advisor_recommendation_click) via the dataLayer / gtag.
class AdvisorWidget extends HTMLElement {
  connectedCallback() {
    this.workerUrl = this.dataset.workerUrl.replace(/\/$/, '');
    this.source = this.dataset.source || 'page';
    this.launcher = this.querySelector('.advisor__launcher');
    this.panel = this.querySelector('.advisor__panel');
    this.messagesEl = this.querySelector('.advisor__messages');
    this.form = this.querySelector('.advisor__form');
    this.input = this.querySelector('.advisor__input');
    this.opened = false;
    // Session id and history are loaded lazily in ensureState(): a plain page
    // view must not read or write browser storage (§ 25 TDDDG).
    this.history = [];

    this.contactUrl = this.dataset.contactUrl || '/pages/hilfe-kontakt';
    this.contactLabel = this.dataset.contactLabel || 'Lieber persönlich? Schreib uns.';
    this.examples = (this.dataset.examples || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!this.examples.length) this.examples = [this.input.placeholder];
    // Once a question has been asked the input invites a follow-up instead of
    // cycling the example queries (Michael 2026-09-21: examples belong to the
    // PDP entry field, the panel should ask for details or follow-ups).
    this.followupPlaceholder = this.dataset.followupPlaceholder || 'Frag nach oder nenn mehr Details, z. B. „eher heller“ oder „gibt es das als Paar?“';

    this.launcher.addEventListener('click', () => this.toggle(true));
    this.querySelector('.advisor__close').addEventListener('click', () => this.toggle(false));

    // Any element on the page carrying data-advisor-open acts as a trigger, so
    // entry points can be placed as custom_liquid blocks (e.g. under the buy
    // buttons) without touching this section. data-advisor-source overrides the
    // reported source, data-advisor-prompt sends a question straight away.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-advisor-open]');
      if (!trigger || this.contains(trigger)) return;
      e.preventDefault();
      if (trigger.dataset.advisorSource) this.source = trigger.dataset.advisorSource;
      this.toggle(true);
      if (trigger.dataset.advisorPrompt) this.send(trigger.dataset.advisorPrompt);
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.send(this.input.value.trim());
    });
  }

  track(event, params) {
    const payload = Object.assign({ event: event, advisor_session_id: this.sessionId }, params);
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', event, params || {});
    }
  }

  // First use of the advisor: only now is browser storage touched.
  ensureState() {
    if (this.sessionId) return;
    this.sessionId = sessionStorage.getItem('advisor:session') || self.crypto.randomUUID();
    sessionStorage.setItem('advisor:session', this.sessionId);
    this.history = JSON.parse(sessionStorage.getItem('advisor:history') || '[]');
  }

  toggle(open) {
    if (open) this.ensureState();
    this.panel.hidden = !open;
    // Locks page scroll while the full-screen sheet is open on phones (CSS).
    document.body.classList.toggle('advisor-open', open);
    this.launcher.setAttribute('aria-expanded', String(open));
    if (open) {
      if (!this.opened) {
        this.opened = true;
        this.track('advisor_open', { source: this.source });
        if (this.history.length === 0 && this.dataset.greeting) {
          this.addBubble('assistant', this.dataset.greeting);
        } else {
          this.restoreHistory();
        }
      }
      if (this.history.some((m) => m.role === 'user')) {
        this.showFollowupPlaceholder();
      } else {
        this.startPlaceholderCycle();
      }
      this.input.focus();
    }
  }

  showFollowupPlaceholder() {
    clearTimeout(this.placeholderTimer);
    this.placeholderTimer = 'stopped';
    this.input.placeholder = this.followupPlaceholder;
  }

  // Types the example queries out character by character so the empty input
  // suggests what the advisor can actually be asked. Stops for good as soon as
  // the visitor types, and never runs when reduced motion is requested.
  startPlaceholderCycle() {
    if (this.placeholderTimer || !this.examples.length) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const stop = () => {
      clearTimeout(this.placeholderTimer);
      this.placeholderTimer = 'stopped';
      this.input.placeholder = this.examples[0];
    };
    this.input.addEventListener('input', stop, { once: true });

    let example = 0;
    let chars = 0;
    let erasing = false;
    const step = () => {
      const text = this.examples[example];
      if (!erasing) {
        chars++;
        this.input.placeholder = text.slice(0, chars);
        if (chars >= text.length) {
          erasing = true;
          this.placeholderTimer = setTimeout(step, 2200);
          return;
        }
      } else {
        chars -= 3;
        if (chars <= 0) {
          chars = 0;
          erasing = false;
          example = (example + 1) % this.examples.length;
        }
        this.input.placeholder = text.slice(0, chars);
      }
      this.placeholderTimer = setTimeout(step, erasing ? 25 : 55);
    };
    this.placeholderTimer = setTimeout(step, 400);
  }

  restoreHistory() {
    if (this.messagesEl.childElementCount > 0) return;
    for (const m of this.history) this.addBubble(m.role, m.content);
  }

  addBubble(role, text) {
    const el = document.createElement('div');
    el.className = 'advisor__bubble advisor__bubble--' + role;
    el.textContent = text;
    this.messagesEl.appendChild(el);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    return el;
  }

  // Clicked products count as implicit likes: the backend biases the semantic
  // ranking toward them (last 10, this session only).
  rememberLike(handle) {
    const liked = JSON.parse(sessionStorage.getItem('advisor:liked') || '[]').filter((h) => h !== handle);
    liked.push(handle);
    sessionStorage.setItem('advisor:liked', JSON.stringify(liked.slice(-10)));
  }

  addProducts(products) {
    if (!products.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'advisor__products';
    products.forEach((p, i) => {
      const card = document.createElement('a');
      card.className = 'advisor__product';
      card.href = p.url;
      const price = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(p.price_eur);
      if (!p.image) card.classList.add('advisor__product--noimage');
      card.innerHTML =
        (p.image ? '<img src="' + p.image + '&width=200" alt="" loading="lazy">' : '') +
        '<span class="advisor__product-text">' +
        '<span class="advisor__product-title"></span>' +
        '<span class="advisor__product-price">' + price + (p.available ? '' : ' · verkauft') + '</span>' +
        '</span>';
      card.querySelector('.advisor__product-title').textContent = p.title;
      card.addEventListener('click', () => {
        this.track('advisor_recommendation_click', { product_id: p.id, position: i + 1 });
        if (p.handle) this.rememberLike(p.handle);
        navigator.sendBeacon(
          this.workerUrl + '/event',
          JSON.stringify({ session_id: this.sessionId, type: 'click', product_id: p.id, handle: p.handle || null, position: i + 1 })
        );
      });
      wrap.appendChild(card);
    });
    this.messagesEl.appendChild(wrap);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  async send(text) {
    if (!text || this.pending) return;
    this.ensureState();
    this.pending = true;
    this.input.value = '';
    this.showFollowupPlaceholder();
    this.addBubble('user', text);
    this.history.push({ role: 'user', content: text });
    const turn = this.history.filter((m) => m.role === 'user').length;
    this.track('advisor_message', { turn_number: turn });
    const typing = this.addBubble('assistant', '…');

    try {
      const res = await fetch(this.workerUrl + '/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          session_id: this.sessionId,
          source: this.source,
          messages: this.history.slice(-12),
          liked_handles: JSON.parse(sessionStorage.getItem('advisor:liked') || '[]'),
          // Tells the worker this widget renders link chips; older widgets get
          // the links appended to the reply text instead.
          features: ['links'],
        }),
      });
      if (res.status === 429) throw new Error('rate_limited');
      if (res.status === 503) throw new Error('budget_exceeded');
      if (!res.ok || !res.body) throw new Error('HTTP ' + res.status);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let reply = '';
      let products = [];
      let links = [];
      let streamError = null;

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are separated by a blank line.
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          const line = frame.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch (err) { continue; }
          if (ev.type === 'status') {
            if (!reply) { typing.textContent = ev.text; typing.classList.add('advisor__bubble--status'); }
          } else if (ev.type === 'delta') {
            if (!reply) typing.classList.remove('advisor__bubble--status');
            reply += ev.text;
            typing.textContent = reply;
            this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
          } else if (ev.type === 'done') {
            products = ev.products || [];
            links = ev.links || [];
          } else if (ev.type === 'error') {
            streamError = ev.error;
          }
        }
      }
      if (streamError || !reply) throw new Error(streamError || 'leere Antwort');

      this.history.push({ role: 'assistant', content: reply });
      sessionStorage.setItem('advisor:history', JSON.stringify(this.history.slice(-12)));
      if (products.length) {
        this.addProducts(products);
        this.track('advisor_recommendation_shown', {
          product_ids: products.map((p) => p.id).join(','),
          count: products.length,
        });
      }
      this.addLinks(links);
    } catch (e) {
      typing.classList.remove('advisor__bubble--status');
      const messages = {
        rate_limited: 'Das waren gerade viele Fragen auf einmal. Warte einen Moment und frag dann nochmal.',
        budget_exceeded: 'Der Berater macht gerade Pause. Schreib uns direkt, wir antworten persönlich.',
      };
      typing.textContent =
        messages[e.message] || 'Da ist etwas schiefgegangen. Versuch es nochmal — oder schreib uns direkt.';
      this.addContactOffer();
    } finally {
      this.pending = false;
    }
  }

  // Collections, guides and articles the answer is based on. Only URLs the
  // worker validated against its tool results arrive here.
  addLinks(links) {
    if (!links.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'advisor__links';
    links.forEach((l, i) => {
      const a = document.createElement('a');
      a.className = 'advisor__link';
      a.href = l.url;
      a.textContent = l.title;
      a.addEventListener('click', () => {
        this.track('advisor_link_click', { link_url: l.url, link_type: l.type, position: i + 1 });
        navigator.sendBeacon(
          this.workerUrl + '/event',
          JSON.stringify({ session_id: this.sessionId, type: 'link', handle: new URL(l.url).pathname, position: i + 1 })
        );
      });
      wrap.appendChild(a);
    });
    this.messagesEl.appendChild(wrap);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // A dead end must never be the last thing a visitor sees: after a failure the
  // human route is offered right in the thread. The permanent link in the
  // footer of the panel covers the "advisor cannot help" case.
  addContactOffer() {
    if (this.messagesEl.querySelector('.advisor__contact-offer')) return;
    const a = document.createElement('a');
    a.className = 'advisor__contact-offer';
    a.href = this.contactUrl;
    a.textContent = this.contactLabel;
    this.messagesEl.appendChild(a);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }
}

customElements.define('advisor-widget', AdvisorWidget);